/**
 * Centova Cast stream endpoints with ordered failover (issue #52).
 *
 * Every KPKL station is provisioned on a single Centova host (cast4) today,
 * which makes that one box a single point of failure for the whole site.
 * Each station therefore carries an ORDERED chain of candidate endpoints:
 * the primary cast4 proxy URL first, then the same proxy path on the
 * provider's sibling Centova hosts (cast1–cast6, distinct IPs verified
 * 2026-08-17). The siblings currently return 404 — the station accounts are
 * not mirrored there yet — so the chain degrades exactly like it does today
 * until an account is mirrored to another host (reseller work, see
 * stations.md). From that moment on, the player and the metadata proxy pick
 * the mirror up automatically during a cast4 outage; no code change needed.
 *
 * Only HTTPS proxy URLs belong in the browser chain: the Shoutcast servers
 * also answer on plain HTTP/direct ports, but a page served over https
 * cannot play mixed-content audio.
 */

/** Primary Centova host — all stations are provisioned here today. */
export const PRIMARY_CENTOVA_HOST = "cast4.my-control-panel.com";

/**
 * Failover hosts in preference order (same provider, distinct IPs/regions).
 * A sibling returns 404 for an account it does not host, so a dead primary
 * fails over cleanly and a healthy mirror takes over as soon as one exists.
 */
export const FAILOVER_CENTOVA_HOSTS = [
  "cast1.my-control-panel.com",
  "cast2.my-control-panel.com",
  "cast3.my-control-panel.com",
  "cast5.my-control-panel.com",
  "cast6.my-control-panel.com",
] as const;

export const CENTOVA_STREAM_HOSTS: readonly string[] = [
  PRIMARY_CENTOVA_HOST,
  ...FAILOVER_CENTOVA_HOSTS,
];

/** Ordered candidate stream URLs for a station's Centova account. */
export function streamEndpointsFor(centovaUser: string): string[] {
  return CENTOVA_STREAM_HOSTS.map(
    host => `https://${host}/proxy/${centovaUser}/stream`
  );
}

/** Ordered candidate stats URLs for a station's Centova account. */
export function statsEndpointsFor(centovaUser: string): string[] {
  return CENTOVA_STREAM_HOSTS.map(
    host => `https://${host}/proxy/${centovaUser}/stats`
  );
}

/**
 * The Pickldiction Podcast channel (issue #106): a dedicated 24/7 podcast
 * channel that is NEVER mixed into a music station's rotation. The episode
 * catalog is the source of truth at tomahawk000/picklediction
 * (client/src/pages/PodcastPage.tsx EPISODES + client/public/audio/); the
 * channel entry below mirrors that catalog so the picker can join the loop
 * mid-episode. Episode additions upstream mean editing BOTH the picklediction
 * page and this list — or replacing it with a shared feed when one exists.
 *
 * Verified live 2026-11-01: the Vercel-hosted MP3s serve audio/mpeg with
 * CORS `access-control-allow-origin: *`, so the same <audio> element that
 * plays Centova streams can play them (player sets crossOrigin="anonymous").
 */
export interface PodcastEpisode {
  /** Episode number, matches picklediction PodcastPage (`n`). */
  n: number;
  /** Episode slug, matches picklediction PodcastPage (`id`). */
  id: string;
  /** Word of the episode, shown as the now-playing title. */
  word: string;
  /** Speakers, shown as the now-playing artist. */
  speakers: string;
  /** Duration label shown in the picker ("8:32"). */
  duration: string;
  /** Direct MP3 URL (CORS-enabled, range-capable). */
  audio: string;
}

const PODCAST_AUDIO_BASE = "https://www.picklediction.com/audio";

export const PODCAST_EPISODES: readonly PodcastEpisode[] = [
  {
    n: 1,
    id: "dink",
    word: "Dink",
    speakers: "kali + Master Hypnotist Dominick DeCarlo",
    duration: "8:32",
    audio: `${PODCAST_AUDIO_BASE}/podcast-ep1-dink.mp3`,
  },
  {
    n: 2,
    id: "kitchen",
    word: "Kitchen",
    speakers: "kali + Master Hypnotist Dominick DeCarlo",
    duration: "8:08",
    audio: `${PODCAST_AUDIO_BASE}/podcast-ep2-kitchen.mp3`,
  },
  {
    n: 3,
    id: "pickled",
    word: "Pickled",
    speakers: "kali + Master Hypnotist Dominick DeCarlo",
    duration: "8:17",
    audio: `${PODCAST_AUDIO_BASE}/podcast-ep3-pickled.mp3`,
  },
  {
    n: 4,
    id: "banger",
    word: "Banger",
    speakers: "kali + Master Hypnotist Dominick DeCarlo",
    duration: "8:44",
    audio: `${PODCAST_AUDIO_BASE}/podcast-ep4-banger.mp3`,
  },
  {
    n: 5,
    id: "third-shot-drop",
    word: "Third Shot Drop",
    speakers: "kali + Master Hypnotist Dominick DeCarlo",
    duration: "8:44",
    audio: `${PODCAST_AUDIO_BASE}/podcast-ep5-third-shot-drop.mp3`,
  },
];

/**
 * Join-in-progress offset for the 24/7 loop: maps wall-clock time onto the
 * catalog so every listener lands mid-episode wherever they tune in
 * (issue #106 acceptance: "wherever they join in, they join in"). Lengths
 * are the measured MP3 durations rounded down (ffprobe 2026-11-01).
 */
export function podcastLoopPosition(nowMs: number = Date.now()): {
  episodeIndex: number;
  offsetSeconds: number;
} {
  const EPISODE_SECONDS = [512, 485, 487, 539, 521];
  const total = EPISODE_SECONDS.reduce((a, b) => a + b, 0);
  let t = Math.floor(nowMs / 1000) % total;
  for (let i = 0; i < EPISODE_SECONDS.length; i++) {
    if (t < EPISODE_SECONDS[i]) return { episodeIndex: i, offsetSeconds: t };
    t -= EPISODE_SECONDS[i];
  }
  return { episodeIndex: 0, offsetSeconds: 0 };
}

/** Public Centova accounts currently configured in the station picker. */
export const CENTOVA_STATION_ACCOUNTS = {
  eclectic: "wesellpickleball",
  "classic-rock": "rockthecourt",
  country: "dinkandtwang",
  hiphop: "dinkdontstop",
  pop: "popanddink",
  edm: "smashanddrop",
  christian: "graceanddink",
  "wind-down": "winddown",
} as const;

const CONFIGURED_STATS_URLS = new Set(
  Object.values(CENTOVA_STATION_ACCOUNTS).flatMap(statsEndpointsFor)
);

/** Exact destinations only: no query, credentials, port, fragment or redirects. */
export function isConfiguredStatsUrl(value: string): boolean {
  return CONFIGURED_STATS_URLS.has(value);
}
